import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { X, Plus, User, Zap, Wrench, Server, BookOpen, GitBranch, Box, ShieldAlert, ArrowLeft } from 'lucide-react';
import apiClient from '../../api/client';
import type {
  AssetType,
  MarketplaceAsset,
  SecuritySeverity,
  SecurityVerdict,
  SimilarAssetItem,
  ComposeAssetItem,
  AssetRelatedResponse,
} from '../../types/marketplace';

const ASSET_ICONS: Record<AssetType, typeof User> = {
  persona: User,
  skill: Zap,
  tool: Wrench,
  mcp: Server,
  knowledge: BookOpen,
  workflow: GitBranch,
};

const ASSET_LABELS: Record<AssetType, string> = {
  persona: '人设',
  skill: '技能',
  tool: '工具',
  mcp: 'MCP 工具',
  knowledge: '知识',
  workflow: '工作流',
};

const SOURCE_LABELS: Record<string, string> = {
  builtin: '内置',
  plugins: '精选插件',
  derived: '衍生',
  custom: '自定义',
  marketplace: '市场',
  manual: '手动',
};

/** 人设 9-Section 字段（含 legacy 字段外的标准段） */
const PERSONA_SECTIONS: Array<[string, string]> = [
  ['identity', '身份定位'],
  ['mission', '使命目标'],
  ['rules', '行为规则'],
  ['deliverables', '交付物'],
  ['workflow', '工作流程'],
  ['communication', '沟通风格'],
  ['metrics', '度量指标'],
  ['advanced_capabilities', '进阶能力'],
];

/** 安全扫描（B11）三档徽标与严重度标签 */
const SECURITY_VERDICT_STYLES: Record<SecurityVerdict, { label: string; background: string; color: string }> = {
  SAFE: { label: '安全', background: 'var(--color-success)', color: 'var(--text-on-success)' },
  CAUTION: { label: '需注意', background: 'var(--color-warning)', color: '#FFFFFF' },
  DO_NOT_INSTALL: { label: '禁止安装', background: 'var(--color-error)', color: '#FFFFFF' },
};

const SEVERITY_LABELS: Record<SecuritySeverity, string> = {
  critical: '致命',
  high: '高危',
  medium: '中危',
  low: '低危',
};

interface AssetPreviewDrawerProps {
  asset: MarketplaceAsset | null;
  onClose: () => void;
  onToggle?: (asset: MarketplaceAsset) => void;
  onApply?: (asset: MarketplaceAsset) => void;
  applyLabel?: string;
  removeLabel?: string;
}

export default function AssetPreviewDrawer({
  asset: initialAsset,
  onClose,
  onToggle,
  onApply,
  applyLabel = '应用',
  removeLabel = '移除',
}: AssetPreviewDrawerProps) {
  // 当前预览目标：初始为父级传入的 asset，点击相关推荐项后可内部切换；
  // history 栈记录切换轨迹，支持「返回上一个预览」。
  const [current, setCurrent] = useState<MarketplaceAsset | null>(initialAsset);
  const [history, setHistory] = useState<MarketplaceAsset[]>([]);
  const [related, setRelated] = useState<AssetRelatedResponse | null>(null);

  // 父级切换预览目标时重置内部状态：渲染期调整（React 推荐的派生状态模式，
  // 避免在 effect 内同步 setState 造成级联渲染）
  const [prevInitial, setPrevInitial] = useState<MarketplaceAsset | null>(initialAsset);
  if (prevInitial !== initialAsset) {
    setPrevInitial(initialAsset);
    setCurrent(initialAsset);
    setHistory([]);
    setRelated(null);
  }

  // B21：按当前预览目标拉取相关推荐（similar_to / compose_with），失败诚实降级不渲染
  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    let request: Promise<{ data: AssetRelatedResponse }>;
    try {
      // Promise.resolve 包裹：防御调用方 mock 返回非 Promise（undefined 等）时同步崩溃
      request = Promise.resolve(apiClient.get<AssetRelatedResponse>(`/asset-templates/${current.id}/related`));
    } catch {
      return;
    }
    request
      .then((res) => {
        if (!cancelled) setRelated(res.data);
      })
      .catch(() => {
        if (!cancelled) setRelated(null);
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  if (!current) return null;

  const Icon = ASSET_ICONS[current.type] ?? Box;
  const isApplied = current.isApplied;

  /** 点击相关推荐项 → 切换预览目标（当前目标压入返回栈）。 */
  const switchToRelated = (item: SimilarAssetItem | ComposeAssetItem) => {
    setHistory((prev) => [...prev, current]);
    setCurrent({
      id: item.id,
      type: item.type,
      name: item.name,
      displayName: item.display_name || item.name,
      payload: {},
    });
  };

  /** 返回上一个预览目标。 */
  const goBack = () => {
    if (history.length === 0) return;
    const restored = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setCurrent(restored);
  };

  /** 按资产类型渲染 payload 要点摘要（人设 9-Section / 技能说明 / MCP 命令 / 知识块数等）。 */
  const renderPayloadSummary = () => {
    const payload = current.payload || {};
    const itemStyle: CSSProperties = { margin: '4px 0', fontSize: 13, lineHeight: 1.6 };
    const labelStyle: CSSProperties = { color: 'var(--text-muted)', marginRight: 6 };

    if (current.type === 'persona') {
      const filled = PERSONA_SECTIONS.filter(([key]) => {
        const v = payload[key];
        return v && (typeof v !== 'object' || Object.keys(v).length > 0);
      });
      const identityText =
        typeof payload.identity === 'string' ? payload.identity : payload.identity?.summary || payload.role;
      return (
        <div style={{ marginBottom: 12 }}>
          {identityText && (
            <p style={itemStyle}>
              <span style={labelStyle}>身份：</span>
              {String(identityText).slice(0, 120)}
            </p>
          )}
          <p style={itemStyle}>
            <span style={labelStyle}>9-Section 覆盖：</span>
            {filled.length > 0 ? filled.map(([, label]) => label).join('、') : '未填写标准段落'}
            {filled.length > 0 && `（${filled.length}/8）`}
          </p>
          {payload.tone && (
            <p style={itemStyle}>
              <span style={labelStyle}>语气：</span>
              {String(payload.tone)}
            </p>
          )}
        </div>
      );
    }

    if (current.type === 'skill') {
      const def = payload.definition || {};
      const description = payload.description || def.description;
      const instructions = payload.instructions || def.instructions;
      return (
        <div style={{ marginBottom: 12 }}>
          {description && (
            <p style={itemStyle}>
              <span style={labelStyle}>技能说明：</span>
              {String(description).slice(0, 160)}
            </p>
          )}
          {instructions && (
            <p style={itemStyle}>
              <span style={labelStyle}>指令摘要：</span>
              {String(instructions).slice(0, 200)}
              {String(instructions).length > 200 ? '…' : ''}
            </p>
          )}
          {!description && !instructions && <p className="text-muted" style={itemStyle}>无技能说明</p>}
        </div>
      );
    }

    if (current.type === 'mcp') {
      const args: string[] = payload.args || [];
      const envKeys = Object.keys(payload.env || {});
      return (
        <div style={{ marginBottom: 12 }}>
          <p style={itemStyle}>
            <span style={labelStyle}>启动命令：</span>
            <code>{payload.command || '未配置'}</code>
          </p>
          {args.length > 0 && (
            <p style={itemStyle}>
              <span style={labelStyle}>参数：</span>
              <code>{args.join(' ')}</code>
            </p>
          )}
          {envKeys.length > 0 && (
            <p style={itemStyle}>
              <span style={labelStyle}>环境变量：</span>
              {envKeys.join('、')}（{envKeys.length} 项）
            </p>
          )}
        </div>
      );
    }

    if (current.type === 'knowledge') {
      const chunks: unknown[] | undefined = payload.chunks || payload.items;
      const chunkCount = Array.isArray(chunks) ? chunks.length : undefined;
      return (
        <div style={{ marginBottom: 12 }}>
          <p style={itemStyle}>
            <span style={labelStyle}>知识块数量：</span>
            {chunkCount !== undefined ? `${chunkCount} 个` : '未分块（单条内容）'}
          </p>
          {payload.category && (
            <p style={itemStyle}>
              <span style={labelStyle}>知识分类：</span>
              {String(payload.category)}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  /** 安全扫描摘要（B11）：score/verdict + 按严重度计数 + 命中明细列表。 */
  const renderSecurityScan = () => {
    const scan = current.securityScan;
    if (!scan) return null;
    const verdict = SECURITY_VERDICT_STYLES[scan.verdict];
    const bySeverity = scan.by_severity || {};
    const sevOrder: SecuritySeverity[] = ['critical', 'high', 'medium', 'low'];
    const shown = (scan.findings || []).slice(0, 8);
    const itemStyle: CSSProperties = { margin: '4px 0', fontSize: 13, lineHeight: 1.6 };
    return (
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>安全扫描</h4>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          {verdict && (
            <span
              className="badge"
              style={{ background: verdict.background, color: verdict.color, fontWeight: 600 }}
            >
              <ShieldAlert size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
              {verdict.label}
            </span>
          )}
          <span className="badge secondary" title={`扫描器 v${scan.scanner_version}，扫描于 ${scan.scanned_at}`}>
            风险分 {scan.score}/100
          </span>
          <span className="badge secondary">命中 {scan.findings_total} 项</span>
          {sevOrder
            .filter((s) => (bySeverity[s] ?? 0) > 0)
            .map((s) => (
              <span key={s} className="badge secondary" style={{ fontSize: 11 }}>
                {SEVERITY_LABELS[s]} {bySeverity[s]}
              </span>
            ))}
        </div>
        {shown.length > 0 && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
            {shown.map((f, idx) => (
              <li key={`${f.pattern_id}-${idx}`} style={itemStyle}>
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 32,
                    marginRight: 6,
                    fontSize: 11,
                    padding: '1px 6px',
                    borderRadius: 100,
                    background:
                      f.severity === 'critical'
                        ? 'var(--color-error)'
                        : f.severity === 'high'
                          ? 'var(--color-warning)'
                          : 'var(--bg-tertiary)',
                    color:
                      f.severity === 'critical' || f.severity === 'high' ? '#FFFFFF' : 'var(--text-secondary)',
                  }}
                >
                  {SEVERITY_LABELS[f.severity] || f.severity}
                </span>
                {f.message}
                {f.excerpt && (
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 2, wordBreak: 'break-all' }}>
                    命中片段：{f.excerpt}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {scan.findings_total > shown.length && (
          <p className="text-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            另有 {scan.findings_total - shown.length} 项命中未展示
          </p>
        )}
      </div>
    );
  };

  const renderPayload = () => {
    const payload = current.payload || {};
    const keys = Object.keys(payload);
    if (keys.length === 0) return <p className="text-muted">无额外配置数据</p>;
    return (
      <pre
        style={{
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          padding: 12,
          fontSize: 12,
          overflow: 'auto',
          maxHeight: 300,
          color: 'var(--text-secondary)',
        }}
      >
        {JSON.stringify(payload, null, 2)}
      </pre>
    );
  };

  /** B21 相关推荐：相似资产（similar_to）+ 组合建议（compose_with），点击切换预览目标。 */
  const renderRelated = () => {
    if (!related) return null; // 加载中/接口失败 → 不渲染（诚实降级）
    const similar = related.similar_to || [];
    const compose = related.compose_with || [];
    if (similar.length === 0 && compose.length === 0) return null;

    const groupLabelStyle: CSSProperties = { margin: '0 0 6px', fontSize: 12, color: 'var(--text-muted)' };
    const itemButtonStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      width: '100%',
      padding: '8px 12px',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-default)',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontSize: 13,
      cursor: 'pointer',
      textAlign: 'left',
    };
    const chipStyle: CSSProperties = { fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 };

    return (
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>相关推荐</h4>
        {similar.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={groupLabelStyle}>相似资产</p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {similar.map((item) => (
                <li key={`similar-${item.id}`}>
                  <button
                    style={itemButtonStyle}
                    onClick={() => switchToRelated(item)}
                    title={`相似度 ${(item.score * 100).toFixed(0)}%，点击预览`}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.display_name || item.name}
                    </span>
                    <span className="text-muted" style={chipStyle}>
                      {ASSET_LABELS[item.type] || item.type} · 相似 {(item.score * 100).toFixed(0)}%
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {compose.length > 0 && (
          <div>
            <p style={groupLabelStyle}>组合建议</p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {compose.map((item) => (
                <li key={`compose-${item.id}`}>
                  <button
                    style={itemButtonStyle}
                    onClick={() => switchToRelated(item)}
                    title={`与 ${current.displayName} 共同出现在 ${item.count} 个配方中，点击预览`}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.display_name || item.name}
                    </span>
                    <span className="text-muted" style={chipStyle}>
                      {ASSET_LABELS[item.type] || item.type} · {item.count} 个配方共现
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 150,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: 520,
          height: '100dvh',
          background: 'var(--bg-secondary)',
          borderLeft: '1px solid var(--border-default)',
          zIndex: 160,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-default)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div className="flex items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--radius-lg)',
                background: 'rgba(88, 166, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={20} color="var(--color-primary)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.displayName}</h3>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {ASSET_LABELS[current.type]} · {current.category || '未分类'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <button
                className="secondary"
                style={{ padding: '6px 8px' }}
                onClick={goBack}
                title="返回上一个预览"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <button className="secondary" style={{ padding: '6px 8px' }} onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {current.description && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>描述</h4>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{current.description}</p>
            </div>
          )}

          {renderSecurityScan()}

          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>来源</h4>
            <span className="badge info">{(current.source ? SOURCE_LABELS[current.source] : undefined) || current.source || '未知来源'}</span>
            {current.presetPack && (
              <span className="badge info" style={{ marginLeft: 8 }} title={`预置资产包：${current.presetPack}`}>
                预置包 · {current.presetPack}
              </span>
            )}
            {current.license && (
              <span className="badge secondary" style={{ marginLeft: 8 }} title={`许可证：${current.license}`}>
                {current.license}
              </span>
            )}
            {current.isEnabled === false && <span className="badge secondary" style={{ marginLeft: 8 }}>已禁用</span>}
          </div>

          {current.tags && current.tags.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>标签</h4>
              <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                {current.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 100,
                      background: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      fontSize: 12,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {renderRelated()}

          {current.metadata && Object.keys(current.metadata).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>元数据</h4>
              <pre
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  padding: 12,
                  fontSize: 12,
                  overflow: 'auto',
                  color: 'var(--text-secondary)',
                }}
              >
                {JSON.stringify(current.metadata, null, 2)}
              </pre>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>配置详情</h4>
            {renderPayloadSummary()}
            {renderPayload()}
          </div>
        </div>

        {(onToggle || onApply) && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-default)',
              display: 'flex',
              gap: 12,
            }}
          >
            {onToggle ? (
              <button
                className={isApplied ? 'danger' : 'primary'}
                style={{ flex: 1 }}
                onClick={() => {
                  onToggle(current);
                  onClose();
                }}
              >
                {isApplied ? (
                  <>
                    <X size={14} /> {removeLabel}
                  </>
                ) : (
                  <>
                    <Plus size={14} /> {applyLabel}
                  </>
                )}
              </button>
            ) : (
              <button
                className="primary"
                style={{ flex: 1 }}
                onClick={() => {
                  onApply?.(current);
                  onClose();
                }}
              >
                <Plus size={14} /> 应用到 Agent
              </button>
            )}
            <button className="secondary" style={{ flex: 1 }} onClick={onClose}>
              关闭
            </button>
          </div>
        )}
      </div>
    </>
  );
}
